import {
    createContext,
    useContext,
    useCallback,
    useEffect,
    useState,
    useMemo,
} from 'react';
import { Button, notification, Tooltip } from 'antd';

import useAPI from '../../hooks/integrations/useAPI';
import { API_BASE_URL } from '../../settings/constants';
import { TableSkeleton } from '../../components/UI/Table/infiniteTableHelpers';
import { useAuth } from '../Auth';
import replaceValues from './../../utils/demoNames';

const Context = createContext();

export const getCompanyName = (companies, uuid, height) => {
    if (companies.isLoading) {
        return (
            <Tooltip title="Companies loading">
                <div>
                    <TableSkeleton size="small" $height={height} active block />
                </div>
            </Tooltip>
        );
    }
    const name = companies.getCompanyByUuid(uuid)?.name;
    if (name) return name;
    if (companies.isError)
        return <Tooltip title="Couldn't fetch companies data">{uuid}</Tooltip>;
    return uuid;
};

const Provider = ({ children }) => {
    const api = useAPI();
    const auth = useAuth();

    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    const [allMap, setAllMap] = useState(new Map());
    const all = useMemo(() => Array.from(allMap.values()), [allMap]);

    const getMetrics = (body) => {
        const url = `${API_BASE_URL}/mastermetrics`;
        console.log('C2.API_POST: ', url);

        return api
            .makeRequest(
                {
                    url: url,
                    method: 'POST',
                    data: JSON.stringify(body),
                },
                false
            )
            .then(({ data }) => data);
    };

    const ErrorNotification = () => {
        const [loading, setLoading] = useState(false);
        const onClick = () => {
            setLoading(true);
            getAll();
        };
        return (
            <div>
                <p>Couldn't get customers information</p>
                <Button loading={loading} onClick={onClick}>
                    Try again
                </Button>
            </div>
        );
    };

    const getAll = useCallback(() => {
        setIsLoading(true);
        console.log('C1.API_GET: ', `${API_BASE_URL}/companies`);

        api.makeRequest({ url: `${API_BASE_URL}/companies`, method: 'GET' })
            .then(({ data }) => {
                data = replaceValues(data, 'name');
                setAllMap(
                    new Map(data.map((company) => [company.uuid, company]))
                );
                notification.close('customerError');
                setIsError(false);
                setIsLoading(false);
            })
            .catch((err) => {
                notification.close('customerError');
                setTimeout(
                    () =>
                        notification.error({
                            key: 'customerError',
                            description: <ErrorNotification key={Date.now()} />,
                            duration: null,
                        }),
                    100
                );

                setIsError(true);
                setIsLoading(false);
            });
    }, [auth.user]);

    const getCompanyByUuid = (uuid) => {
        return allMap.get(uuid);
    };

    const getInsightsPlots = (params, options) => {
        const url = new URL(`${API_BASE_URL}/insights-plots`);
        console.info('C4.API_POST :', url.toString() + ', params:', params);
        return api
            .makeRequest(
                {
                    url,
                    method: 'POST',
                    data: JSON.stringify(params),
                },
                options
            )
            .then(({ data }) => data);
    };

    const [userInfo, setUserInfo] = useState({});

    const getPermissions = () => {
        const url = `${API_BASE_URL}/user-data`;

        api.makeRequest({ url }).then(({ data }) => {
            const {
                company,
                can_edit_machines,
                can_edit_users,
                can_see_all,
                ...rest
            } = data;

            setUserInfo({
                ...rest,
                company: company && getCompanyByUuid(company)?.name,
                canSeeMultiple: can_see_all,
                canEditMachines: can_edit_machines,
                isAdmin: can_edit_users,
            });
        });
    };

    const getPLXUids = () => {
        return ['cQZ6rRSH99ZdGotWBAdxGD'];
        // const plxNames = ['plx', 'plastometrex'];
        // return all
        //     .filter(({ name }) => plxNames.includes(name.toLowerCase().trim()))
        //     .map(({ uuid }) => uuid);
    };

    useEffect(() => {
        if (auth.user) {
            getAll();
            getPermissions();
        }
    }, [auth.user]);

    useEffect(() => {
        if (auth.user) {
            getPermissions();
        }
    }, [all]);

    return (
        <Context.Provider
            value={{
                all,
                isLoading,
                isError,
                getCompanyByUuid,
                getInsightsPlots,
                getMetrics,
                getAll,
                getPLXUids,
                userInfo,
            }}
        >
            {children}
        </Context.Provider>
    );
};

export const useCompanyCtx = () => {
    return useContext(Context);
};

export default Provider;
